import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Header from "./header";
import moment from "moment";
import XLSX from "xlsx-js-style";
import ApiConfig from "../../common/config/ApiConfig";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLanguage } from "../../common/i18n/LanguageContext";
import Tooltip from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ViewListIcon from "@mui/icons-material/ViewList";
import MoneyIcon from "@mui/icons-material/Money";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import CalculateIcon from "@mui/icons-material/Calculate";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

// Consistent color per line "role" so users can visually tell the different
// inputs of a formula apart (e.g. rate vs credit vs excess) across tooltips.
const TOOLTIP_LINE_COLORS = {
  stake: "text-slate-600",
  pct: "text-sky-600",
  amount: "text-teal-600",
  fightLimit: "text-amber-600",
  excess: "text-red-600",
  commissionBase: "text-slate-600",
  netCommission: "text-green-600",
  netRatePct: "text-sky-600",
  positionNet: "text-teal-600",
  turnoverRef: "text-teal-600",
  payoutRate: "text-amber-600",
  netAmtRef: "text-green-600",
  winAmount: "text-green-600",
  commissionAmount: "text-teal-600",
};

const TOOLTIP_RESULT_COLOR = "text-blue-700";

const wlResultColor = (value) =>
  toNumber(value) < 0 ? "text-red-600" : TOOLTIP_RESULT_COLOR;

// Builds a "label = term(value) <op> term(value) = result" line with each
// term color-coded by role, so it's clear at a glance which number is which
// without having to parse plain text. A term can be `{plain: "100%"}` for a
// bare literal, or `{label, value, color}` for a "label(value)" pair.
// `result` is optional — omit it when a separate element right after this
// one already shows the final "= value" (avoids showing it twice back to
// back).
const combineLine = (label, terms, opSymbol, result) => (
  <>
    {label}
    {" = "}
    {terms.map((term, i) => (
      <React.Fragment key={i}>
        {i > 0 && ` ${opSymbol} `}
        {term.plain !== undefined ? (
          term.plain
        ) : (
          <span className={term.color}>
            {term.label}({term.value})
          </span>
        )}
      </React.Fragment>
    ))}
    {result !== undefined && (
      <>
        {" "}
        = <span className={TOOLTIP_RESULT_COLOR}>{result}</span>
      </>
    )}
  </>
);

// Which tiers' turnover/commission feed into a given tier's net_commission /
// position_taking_net formulas, per api/docs/CALCULATE_POSITION_TAKING.md §4/§6
// (nearest downline first).
const DOWNLINE_TIERS = {
  agent: [],
  master: ["agent"],
  senior: ["master", "agent"],
  company: ["senior", "master", "agent"],
};

const TIER_LABEL_KEY = {
  company: "winlose_col_company",
  senior: "winlose_col_senior",
  master: "winlose_col_master",
  agent: "winlose_col_agent",
};

const lottoTypeKeyMap = {
  th_gov: "table_lotto_thai",
  laos_gov: "table_lotto_laos",
  hanoy_PhuQua: "lotto_type_hanoi",
  Yeekee_5: "table_lotto_yeekee5",
  Yeekee_10: "table_lotto_yeekee10",
  Yeekee_15: "table_lotto_yeekee15",
};

const betTypeKeyMap = {
  first: "stake_label_first",
  four_top: "stake_label_four_top",
  four_tod: "stake_label_four_tod",
  three_top: "stake_label_three_top",
  three_front: "stake_label_three_front",
  three_bot: "stake_label_three_bot",
  three_tod: "stake_label_three_tod",
  two_top: "stake_label_two_top",
  two_bot: "stake_label_two_bot",
  run_top: "stake_label_run_top",
  run_bot: "stake_label_run_bot",
  run_top_front: "stake_label_run_top_front",
  run_top_mid: "stake_label_run_top_mid",
  run_top_end: "stake_label_run_top_end",
  run_bot_front: "stake_label_run_bot_front",
  run_bot_mid: "stake_label_run_bot_mid",
  run_bot_end: "stake_label_run_bot_end",
  run_top_big: "stake_label_run_top_big",
  run_top_small: "stake_label_run_top_small",
  run_top_even: "stake_label_run_top_even",
  run_top_odd: "stake_label_run_top_odd",
  run_bot_big: "stake_label_run_bot_big",
  run_bot_small: "stake_label_run_bot_small",
  run_bot_even: "stake_label_run_bot_even",
  run_bot_odd: "stake_label_run_bot_odd",
  run_top_front_even: "stake_label_run_top_front_even",
  run_top_front_odd: "stake_label_run_top_front_odd",
  run_top_front_small: "stake_label_run_top_front_small",
  run_top_front_big: "stake_label_run_top_front_big",
  run_top_mid_even: "stake_label_run_top_mid_even",
  run_top_mid_odd: "stake_label_run_top_mid_odd",
  run_top_mid_small: "stake_label_run_top_mid_small",
  run_top_mid_big: "stake_label_run_top_mid_big",
  run_top_end_even: "stake_label_run_top_end_even",
  run_top_end_odd: "stake_label_run_top_end_odd",
  run_top_end_small: "stake_label_run_top_end_small",
  run_top_end_big: "stake_label_run_top_end_big",
  run_bot_front_even: "stake_label_run_bot_front_even",
  run_bot_front_odd: "stake_label_run_bot_front_odd",
  run_bot_front_small: "stake_label_run_bot_front_small",
  run_bot_front_big: "stake_label_run_bot_front_big",
  run_bot_mid_even: "stake_label_run_bot_mid_even",
  run_bot_mid_odd: "stake_label_run_bot_mid_odd",
  run_bot_mid_small: "stake_label_run_bot_mid_small",
  run_bot_mid_big: "stake_label_run_bot_mid_big",
  run_bot_end_even: "stake_label_run_bot_end_even",
  run_bot_end_odd: "stake_label_run_bot_end_odd",
  run_bot_end_small: "stake_label_run_bot_end_small",
  run_bot_end_big: "stake_label_run_bot_end_big",
  step_front_mid_end: "stake_label_step_front_mid_end",
  step_mix: "stake_label_step_mix",
};

const toNumber = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

const formatAmount = (value) => {
  const num = parseFloat(value);
  return isNaN(num)
    ? ""
    : num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

const wlClass = (value) => {
  const num = parseFloat(value);
  return `font-bold ${!isNaN(num) && num < 0 ? "text-red-600" : "text-blue-600"}`;
};

// LottoNumber.number is a plain digit string for most bet types, but
// step_front_mid_end/step_mix store it as a JSON-encoded { first, second,
// third } object of picks — each pick is either a category ("big"/"small"/
// "even"/"odd") or a literal digit "0"-"9" (see stepBet.util.ts). Renders
// the categories translated and joined, e.g. "สูง / ต่ำ / คี่" — a plain
// digit pick passes through unchanged since it's not in the lookup.
const STEP_PICK_LABEL_KEY = {
  big: "step_pick_big",
  small: "step_pick_small",
  even: "step_pick_even",
  odd: "step_pick_odd",
};

const formatNumberDisplay = (t, numberStr) => {
  if (typeof numberStr !== "string" || !numberStr.startsWith("{")) {
    return numberStr;
  }
  try {
    const picks = JSON.parse(numberStr);
    return ["first", "second", "third"]
      .filter((key) => key in picks)
      .map((key) => {
        const code = picks[key];
        const labelKey = STEP_PICK_LABEL_KEY[code];
        return labelKey ? t(labelKey) : code;
      })
      .join(" / ");
  } catch {
    return numberStr;
  }
};

// /orderhistory now returns a flat array of individual bet lines (one entry
// per LottoNumber row) directly — already scoped/paginated server-side at
// the bet-line grain, so each item in `data` is exactly one displayable row.
// Member's own turnover/net_amount/wl come straight from LottoNumber's
// member_turnover/member_net_amount/member_wl columns (per
// CALCULATE_POSITION_TAKING.md §5) — not derived by summing the tiers'
// positionTakings, which only approximates member's real figure and drifts
// once any tier's fight_limit saturates (turnover no longer sums to stake).
const buildTierGroup = (pt) =>
  pt && {
    username: pt.username,
    turnover: toNumber(pt.turnover),
    netAmt: toNumber(pt.net_amount),
    wl: toNumber(pt.wl),
    pct: toNumber(pt.position_taking_percent),
    commissionPct: toNumber(pt.commission_percent),
    fightLimitCredit: toNumber(pt.fight_limit_credit),
    fightLimit: toNumber(pt.fight_limit),
    positionTakingAmount: toNumber(pt.position_taking_amount),
    excess: toNumber(pt.position_taking_excess),
    netCommission: toNumber(pt.net_commission),
    positionTakingNet: toNumber(pt.position_taking_net),
  };

const buildRows = (betLines) => {
  return (betLines || []).map((line) => {
    const positionTakings = line.positionTakings || [];
    const company = positionTakings.find((pt) => pt.position === "company");
    const senior = positionTakings.find((pt) => pt.position === "senior");
    const master = positionTakings.find((pt) => pt.position === "master");
    const agent = positionTakings.find((pt) => pt.position === "agent");

    return {
      key: `${line.order_id}-${line.id}`,
      drawDate: line.resultdate,
      market: line.type,
      drawId: line.order_id,
      ticketNo: line.id,
      transactionTime: line.buydate,
      player: line.member,
      game: line.bet_type,
      number: line.number,
      rewards: toNumber(line.bet_payrate),
      stake: toNumber(line.bet_amout),
      result: line.result,
      winAmout: toNumber(line.win_amout),
      member: {
        turnover: toNumber(line.member_turnover),
        netAmt: toNumber(line.member_net_amount),
        wl: toNumber(line.member_wl),
        commissionPct: toNumber(line.member_commission_percent),
        commissionAmount: toNumber(line.member_commission_amount),
      },
      company: buildTierGroup(company),
      senior: buildTierGroup(senior),
      master: buildTierGroup(master),
      agent: buildTierGroup(agent),
    };
  });
};

const TIER_KEYS = ["company", "senior", "master", "agent", "member"];
const ZERO_TIER_TOTALS = TIER_KEYS.reduce((acc, key) => {
  acc[key] = { turnover: 0, netAmt: 0, wl: 0 };
  return acc;
}, {});

// Builds the Excel export worksheet with the same two-row grouped header as
// the on-screen table (tier name spanning its columns, colored per tier,
// with "turnover/net" and "win/loss" sub-headers underneath) and colors
// win/loss figures red when negative / blue when positive — mirroring
// wlClass()'s red/blue rule used on screen.
const FIXED_COLUMNS = [
  { label: "#" },
  { labelKey: "lottobet_draw_date_label" },
  { labelKey: "lottobet_market_label" },
  { labelKey: "orderhistory_col_draw_id" },
  { labelKey: "orderhistory_col_ticket_no" },
  { labelKey: "orderhistory_col_transaction_time" },
  { labelKey: "common_member" },
  { labelKey: "lottobet_bet_type_label" },
  { labelKey: "common_number_label" },
  { labelKey: "waitresult_payout_rate_label" },
  { labelKey: "winlose_col_bet_amount" },
];

const EXPORT_TIER_GROUPS = [
  { key: "company", labelKey: "winlose_col_company", color: "0F766E" },
  { key: "senior", labelKey: "winlose_col_senior", color: "B45309" },
  { key: "master", labelKey: "winlose_col_master", color: "15803D" },
  { key: "agent", labelKey: "winlose_col_agent", color: "1E293B" },
  { key: "member", labelKey: "common_member", color: "1E40AF" },
];

const TIER_COLS = 3; // turnover, netAmt, winloss
const TOTAL_COLS = FIXED_COLUMNS.length + EXPORT_TIER_GROUPS.length * TIER_COLS;
const AMOUNT_FORMAT = "#,##0.00";
const BORDER = { style: "thin", color: { rgb: "CBD5E1" } };
const THIN_BORDERS = {
  top: BORDER,
  bottom: BORDER,
  left: BORDER,
  right: BORDER,
};

const buildExportWorksheet = (t, rows, totals) => {
  const headerRow0 = new Array(TOTAL_COLS).fill("");
  const headerRow1 = new Array(TOTAL_COLS).fill("");

  FIXED_COLUMNS.forEach((col, i) => {
    headerRow0[i] = col.label || t(col.labelKey);
  });

  EXPORT_TIER_GROUPS.forEach((group, i) => {
    const start = FIXED_COLUMNS.length + i * TIER_COLS;
    headerRow0[start] = t(group.labelKey);
    headerRow1[start] =
      `${t("orderhistory_col_turnover")}\n${t("orderhistory_col_net_amt")}`;
    headerRow1[start + 2] = t("winlose_col_win_loss");
  });

  const dataRows = rows.map((row, index) => {
    const line = new Array(TOTAL_COLS).fill("");
    line[0] = index + 1;
    line[1] = row.drawDate ? moment(row.drawDate).format("YYYY-MM-DD") : "";
    line[2] = lottoTypeKeyMap[row.market]
      ? t(lottoTypeKeyMap[row.market])
      : row.market;
    line[3] = row.drawId;
    line[4] = row.ticketNo;
    line[5] = row.transactionTime
      ? moment(row.transactionTime).format("YYYY-MM-DD HH:mm:ss")
      : "";
    line[6] = row.player;
    line[7] = betTypeKeyMap[row.game] ? t(betTypeKeyMap[row.game]) : row.game;
    line[8] = formatNumberDisplay(t, row.number);
    line[9] = toNumber(row.rewards);
    line[10] = toNumber(row.stake);
    EXPORT_TIER_GROUPS.forEach((group, i) => {
      const start = FIXED_COLUMNS.length + i * TIER_COLS;
      const g = row[group.key];
      line[start] = toNumber(g?.turnover);
      line[start + 1] = toNumber(g?.netAmt);
      line[start + 2] = toNumber(g?.wl);
    });
    return line;
  });

  // Bottom total row: the server-computed totals across *every* row matching
  // the filter (not just this page/export batch) — see /orderhistory's
  // `totals` response field.
  const tierTotals = totals || ZERO_TIER_TOTALS;
  const totalsLine = new Array(TOTAL_COLS).fill("");
  totalsLine[0] = t("table_total");
  EXPORT_TIER_GROUPS.forEach((group, i) => {
    const start = FIXED_COLUMNS.length + i * TIER_COLS;
    const g = tierTotals[group.key] || ZERO_TIER_TOTALS[group.key];
    totalsLine[start] = g.turnover;
    totalsLine[start + 1] = g.netAmt;
    totalsLine[start + 2] = g.wl;
  });
  const totalsRowIndex = dataRows.length + 2;

  const worksheet = XLSX.utils.aoa_to_sheet([
    headerRow0,
    headerRow1,
    ...dataRows,
    totalsLine,
  ]);

  const merges = [];
  // Fixed columns: rowSpan 2 (row0 label carries through row1).
  FIXED_COLUMNS.forEach((_, c) => {
    merges.push({ s: { r: 0, c }, e: { r: 1, c } });
  });
  // Tier group name spans all 3 of its sub-columns; the turnover/net-amount
  // sub-header spans its first 2 sub-columns (win/loss stays its own cell).
  EXPORT_TIER_GROUPS.forEach((_, i) => {
    const start = FIXED_COLUMNS.length + i * TIER_COLS;
    merges.push({
      s: { r: 0, c: start },
      e: { r: 0, c: start + TIER_COLS - 1 },
    });
    merges.push({ s: { r: 1, c: start }, e: { r: 1, c: start + 1 } });
  });
  merges.push({
    s: { r: totalsRowIndex, c: 0 },
    e: { r: totalsRowIndex, c: FIXED_COLUMNS.length - 1 },
  });
  worksheet["!merges"] = merges;

  worksheet["!cols"] = FIXED_COLUMNS.map(() => ({ wch: 14 })).concat(
    EXPORT_TIER_GROUPS.flatMap(() => [{ wch: 14 }, { wch: 14 }, { wch: 12 }]),
  );

  const setStyle = (r, c, style) => {
    const ref = XLSX.utils.encode_cell({ r, c });
    if (!worksheet[ref]) worksheet[ref] = { t: "s", v: "" };
    worksheet[ref].s = { ...(worksheet[ref].s || {}), ...style };
  };

  const headerBaseStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: THIN_BORDERS,
  };

  // Fixed-column headers: neutral gray fill, spans both header rows.
  FIXED_COLUMNS.forEach((_, c) => {
    setStyle(0, c, {
      ...headerBaseStyle,
      font: { bold: true, color: { rgb: "1E293B" } },
      fill: { fgColor: { rgb: "E2E8F0" } },
    });
    setStyle(1, c, {
      ...headerBaseStyle,
      font: { bold: true, color: { rgb: "1E293B" } },
      fill: { fgColor: { rgb: "E2E8F0" } },
    });
  });

  // Tier headers: colored fill across all 3 sub-columns on both rows.
  EXPORT_TIER_GROUPS.forEach((group) => {
    const start =
      FIXED_COLUMNS.length + EXPORT_TIER_GROUPS.indexOf(group) * TIER_COLS;
    for (let c = start; c < start + TIER_COLS; c++) {
      setStyle(0, c, {
        ...headerBaseStyle,
        fill: { fgColor: { rgb: group.color } },
      });
      setStyle(1, c, {
        ...headerBaseStyle,
        fill: { fgColor: { rgb: group.color } },
      });
    }
  });

  // Data cells: thin borders + right-aligned amounts; win/loss cells get
  // red (<0) / blue (>0) font color, matching wlClass() on screen.
  const amountCols = new Set([9, 10]);
  EXPORT_TIER_GROUPS.forEach((_, i) => {
    const start = FIXED_COLUMNS.length + i * TIER_COLS;
    amountCols.add(start);
    amountCols.add(start + 1);
  });
  const winLossCols = EXPORT_TIER_GROUPS.map(
    (_, i) => FIXED_COLUMNS.length + i * TIER_COLS + 2,
  );

  dataRows.forEach((line, rowIdx) => {
    const r = rowIdx + 2;
    for (let c = 0; c < TOTAL_COLS; c++) {
      const isAmount = amountCols.has(c);
      const isWinLoss = winLossCols.includes(c);
      const style = { border: THIN_BORDERS };
      if (isAmount || isWinLoss) {
        style.alignment = { horizontal: "right" };
        style.numFmt = AMOUNT_FORMAT;
      }
      if (isWinLoss) {
        const value = line[c];
        style.font = {
          bold: true,
          color: {
            rgb: value < 0 ? "DC2626" : value > 0 ? "2563EB" : "1E293B",
          },
        };
      }
      setStyle(r, c, style);
    }
  });

  // Totals row: bold with a distinct fill; win/loss sums keep the same
  // red/blue rule as the data rows above.
  for (let c = 0; c < TOTAL_COLS; c++) {
    const isAmount = amountCols.has(c);
    const isWinLoss = winLossCols.includes(c);
    const style = {
      border: THIN_BORDERS,
      fill: { fgColor: { rgb: "CBD5E1" } },
      font: { bold: true, color: { rgb: "1E293B" } },
    };
    if (isAmount || isWinLoss) {
      style.alignment = { horizontal: "right" };
      style.numFmt = AMOUNT_FORMAT;
    }
    if (isWinLoss) {
      const value = totalsLine[c];
      style.font = {
        bold: true,
        color: { rgb: value < 0 ? "DC2626" : value > 0 ? "2563EB" : "1E293B" },
      };
    }
    setStyle(totalsRowIndex, c, style);
  }

  return worksheet;
};

// Tooltip content builders — each returns { title, lines[], formula, result }
// built from the row's already-fetched fields so every number is reconstructed
// with the actual figures for that specific bet line, per
// api/docs/CALCULATE_POSITION_TAKING.md.
const buildTierTurnoverTooltip = (t, row, tierKey) => {
  const group = row[tierKey];
  if (!group) return null;
  return {
    title: t("wl_tooltip_title_turnover"),
    lines: [
      {
        label: t("wl_tooltip_stake"),
        value: formatAmount(row.stake),
        color: TOOLTIP_LINE_COLORS.stake,
        icon: "money",
      },
      {
        label: t("wl_tooltip_position_amount"),
        value: (
          <>
            <span className={TOOLTIP_LINE_COLORS.stake}>
              {formatAmount(row.stake)}
            </span>
            {" × "}
            <span className={TOOLTIP_LINE_COLORS.pct}>
              {formatAmount(group.pct)}%
            </span>
          </>
        ),
        result: formatAmount(group.positionTakingAmount),
        color: TOOLTIP_LINE_COLORS.amount,
      },
      {
        label: t("wl_tooltip_fight_limit_credit"),
        value: formatAmount(group.fightLimitCredit),
        color: TOOLTIP_LINE_COLORS.fightLimit,
      },
      {
        label: t("wl_tooltip_excess"),
        value: formatAmount(group.excess),
        color: TOOLTIP_LINE_COLORS.excess,
      },
    ],
    formula: t("wl_tooltip_turnover_formula"),
    result: formatAmount(group.turnover),
    resultColor: TOOLTIP_RESULT_COLOR,
  };
};

const buildTierNetAmtTooltip = (t, row, tierKey) => {
  const group = row[tierKey];
  if (!group) return null;
  const downlineKeys = DOWNLINE_TIERS[tierKey] || [];
  const downlineCommissionPctSum = downlineKeys.reduce(
    (sum, key) => sum + (row[key]?.commissionPct ?? 0),
    0,
  );
  const netRatePct =
    100 - downlineCommissionPctSum - (row.member?.commissionPct ?? 0);

  // Commission base = stake − (this tier's turnover + every downline tier's
  // turnover), per §4/§6's net_commission formula.
  const subtractTiers = [tierKey, ...downlineKeys];
  const commissionBase =
    row.stake -
    subtractTiers.reduce((sum, key) => sum + (row[key]?.turnover ?? 0), 0);
  const commissionBaseTerms = [
    {
      label: t("wl_tooltip_stake"),
      value: formatAmount(row.stake),
      color: TOOLTIP_LINE_COLORS.stake,
    },
    ...subtractTiers.map((key) => ({
      label: t(TIER_LABEL_KEY[key]),
      value: formatAmount(row[key]?.turnover ?? 0),
      color: TOOLTIP_LINE_COLORS.turnoverRef,
    })),
  ];

  const netRateTerms = [
    { plain: "100%" },
    ...downlineKeys.map((key) => ({
      label: t(TIER_LABEL_KEY[key]),
      value: `${formatAmount(row[key]?.commissionPct ?? 0)}%`,
      color: TOOLTIP_LINE_COLORS.pct,
    })),
    {
      label: t("common_member"),
      value: `${formatAmount(row.member?.commissionPct ?? 0)}%`,
      color: TOOLTIP_LINE_COLORS.pct,
    },
  ];

  return {
    title: t("wl_tooltip_title_net_amt"),
    lines: [
      {
        label: t("wl_tooltip_commission_pct"),
        value: `${formatAmount(group.commissionPct)}%`,
        color: TOOLTIP_LINE_COLORS.pct,
      },
      {
        label: t("wl_tooltip_position_net_base_pct"),
        value: `${formatAmount(netRatePct)}%`,
        color: TOOLTIP_LINE_COLORS.netRatePct,
      },
      {
        label: t("wl_tooltip_net_commission"),
        value: formatAmount(group.netCommission),
        color: TOOLTIP_LINE_COLORS.netCommission,
        icon: "calculate",
        details: [
          combineLine(
            t("wl_tooltip_commission_base"),
            commissionBaseTerms,
            "−",
            formatAmount(commissionBase),
          ),
          combineLine(
            t("wl_tooltip_net_commission"),
            [
              {
                label: t("wl_tooltip_commission_base"),
                value: formatAmount(commissionBase),
                color: TOOLTIP_LINE_COLORS.commissionBase,
              },
              {
                label: t("wl_tooltip_commission_pct"),
                value: `${formatAmount(group.commissionPct)}%`,
                color: TOOLTIP_LINE_COLORS.pct,
              },
            ],
            "×",
            formatAmount(group.netCommission),
          ),
        ],
      },
      {
        label: t("wl_tooltip_position_net"),
        value: formatAmount(group.positionTakingNet),
        color: TOOLTIP_LINE_COLORS.positionNet,
        icon: "calculate",
        details: [
          `${t("orderhistory_col_turnover")}: ${formatAmount(group.turnover)}`,
          combineLine(
            t("wl_tooltip_position_net_base_pct"),
            netRateTerms,
            "−",
            `${formatAmount(netRatePct)}%`,
          ),
          combineLine(
            t("wl_tooltip_position_net"),
            [
              {
                label: t("orderhistory_col_turnover"),
                value: formatAmount(group.turnover),
                color: TOOLTIP_LINE_COLORS.turnoverRef,
              },
              {
                label: t("wl_tooltip_position_net_base_pct"),
                value: `${formatAmount(netRatePct)}%`,
                color: TOOLTIP_LINE_COLORS.netRatePct,
              },
            ],
            "×",
            formatAmount(group.positionTakingNet),
          ),
        ],
      },
    ],
    formula: combineLine(
      t("wl_tooltip_net_amount_label"),
      [
        {
          label: t("wl_tooltip_position_net"),
          value: formatAmount(group.positionTakingNet),
          color: TOOLTIP_LINE_COLORS.positionNet,
        },
        {
          label: t("wl_tooltip_net_commission"),
          value: formatAmount(group.netCommission),
          color: TOOLTIP_LINE_COLORS.netCommission,
        },
      ],
      "+",
    ),
    result: formatAmount(group.netAmt),
    resultColor: TOOLTIP_RESULT_COLOR,
  };
};

const buildTierWlTooltip = (t, row, tierKey) => {
  const group = row[tierKey];
  if (!group) return null;
  const isWin = row.result === "win";
  return {
    title: t("wl_tooltip_title_wl"),
    lines: isWin
      ? [
          {
            label: t("orderhistory_col_turnover"),
            value: formatAmount(group.turnover),
            color: TOOLTIP_LINE_COLORS.turnoverRef,
          },
          {
            label: t("waitresult_payout_rate_label"),
            value: formatAmount(row.rewards),
            color: TOOLTIP_LINE_COLORS.payoutRate,
          },
          {
            label: t("orderhistory_col_net_amt"),
            value: formatAmount(group.netAmt),
            color: TOOLTIP_LINE_COLORS.netAmtRef,
          },
        ]
      : [
          {
            label: t("orderhistory_col_net_amt"),
            value: formatAmount(group.netAmt),
            color: TOOLTIP_LINE_COLORS.netAmtRef,
          },
        ],
    formula: isWin
      ? t("wl_tooltip_win_formula")
      : t("wl_tooltip_pending_formula"),
    result: formatAmount(group.wl),
    resultColor: wlResultColor(group.wl),
  };
};

const buildMemberTurnoverTooltip = (t, row) => ({
  title: t("wl_tooltip_title_turnover"),
  lines: [
    {
      label: t("wl_tooltip_stake"),
      value: formatAmount(row.stake),
      color: TOOLTIP_LINE_COLORS.stake,
      icon: "money",
    },
  ],
  formula: t("wl_tooltip_member_turnover_formula"),
  result: formatAmount(row.member.turnover),
  resultColor: TOOLTIP_RESULT_COLOR,
});

const buildMemberNetAmtTooltip = (t, row) => ({
  title: t("wl_tooltip_title_net_amt"),
  lines: [
    {
      label: t("wl_tooltip_stake"),
      value: formatAmount(row.stake),
      color: TOOLTIP_LINE_COLORS.stake,
      icon: "money",
    },
    {
      label: t("wl_tooltip_commission_pct"),
      value: `${formatAmount(row.member.commissionPct)}%`,
      color: TOOLTIP_LINE_COLORS.pct,
    },
    {
      label: t("wl_tooltip_member_commission_amount"),
      value: formatAmount(row.member.commissionAmount),
      color: TOOLTIP_LINE_COLORS.commissionAmount,
    },
  ],
  formula: t("wl_tooltip_member_net_formula"),
  result: formatAmount(row.member.netAmt),
  resultColor: TOOLTIP_RESULT_COLOR,
});

const buildMemberWlTooltip = (t, row) => {
  const isWin = row.result === "win";
  return {
    title: t("wl_tooltip_title_wl"),
    lines: isWin
      ? [
          {
            label: t("wl_tooltip_win_amount"),
            value: formatAmount(row.winAmout),
            color: TOOLTIP_LINE_COLORS.winAmount,
          },
          {
            label: t("wl_tooltip_stake"),
            value: formatAmount(row.stake),
            color: TOOLTIP_LINE_COLORS.stake,
            icon: "money",
          },
          {
            label: t("wl_tooltip_member_commission_amount"),
            value: formatAmount(row.member.commissionAmount),
            color: TOOLTIP_LINE_COLORS.commissionAmount,
          },
        ]
      : [
          {
            label: t("orderhistory_col_net_amt"),
            value: formatAmount(row.member.netAmt),
            color: TOOLTIP_LINE_COLORS.netAmtRef,
          },
        ],
    formula: isWin
      ? t("wl_tooltip_member_win_formula")
      : t("wl_tooltip_member_pending_formula"),
    result: formatAmount(row.member.wl),
    resultColor: wlResultColor(row.member.wl),
  };
};

const TooltipLineIcon = ({ icon }) => {
  if (icon === "money") {
    return <MoneyIcon className="shrink-0" sx={{ fontSize: 14 }} />;
  }
  if (icon === "calculate") {
    return <CalculateIcon className="shrink-0" sx={{ fontSize: 14 }} />;
  }
  return <ArrowRightAltIcon className="shrink-0" sx={{ fontSize: 14 }} />;
};

// A tooltip line that can carry a collapsed-by-default "details" breakdown —
// used for computed values (net commission / net position-taking) so the
// step-by-step formula stays available without cluttering the default view.
// Controlled from the parent so only one line's details can be open at once
// (an accordion) within the same tooltip.
const TooltipLine = ({ line, expanded, onToggle }) => {
  const hasDetails = Array.isArray(line.details) && line.details.length > 0;

  return (
    <div>
      <div
        className={`flex items-center gap-1 ${line.color || "text-slate-600"}`}
      >
        <TooltipLineIcon icon={line.icon} />
        <span className="flex-1">
          {line.label} ({line.value})
          {line.result !== undefined && <> = {line.result}</>}
        </span>
        {hasDetails && (
          <button
            type="button"
            onClick={onToggle}
            className="text-slate-400 hover:text-slate-600 shrink-0"
          >
            <ExpandMoreIcon
              sx={{
                fontSize: 16,
                transform: expanded ? "rotate(180deg)" : "none",
                transition: "transform 0.15s",
              }}
            />
          </button>
        )}
      </div>
      {hasDetails && expanded && (
        <div className="ml-5 mt-0.5 pl-2 border-l-2 border-slate-200 space-y-0.5 text-slate-500">
          {line.details.map((detail, j) => (
            <div key={j} className="flex items-center gap-1">
              <KeyboardArrowRightIcon
                className="shrink-0"
                sx={{ fontSize: 14 }}
              />
              {detail}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const UPLINE_TIER_ORDER = ["company", "senior", "master", "agent"];

// Icon+tooltip next to the member/player cell — shows the bet-time snapshot
// of the whole upline chain's config (position-taking %, fight limit,
// commission %), plus the bet's own reward/payout rate, so users can see at
// a glance what was configured for this specific bet without cross-checking
// each tier's admin page.
const MemberUplineConfigIcon = ({ t, row }) => {
  const [open, setOpen] = useState(false);
  const tiers = UPLINE_TIER_ORDER.filter((key) => row[key]);
  if (tiers.length === 0) return null;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <span className="inline-block">
        <Tooltip
          arrow
          placement="top"
          open={open}
          onClose={() => setOpen(false)}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#ffffff",
                color: "#1e293b",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                maxWidth: 360,
              },
            },
            arrow: {
              sx: {
                color: "#ffffff",
                "&::before": { border: "1px solid #e2e8f0" },
              },
            },
          }}
          title={
            <div className="text-[16px] leading-relaxed">
              <div className="font-bold mb-1 text-slate-800 flex items-center gap-1">
                <AccountTreeIcon
                  className="text-slate-500 shrink-0"
                  sx={{ fontSize: 16 }}
                />
                {t("wl_upline_config_title")}
              </div>
              <div className="mb-1 text-amber-600">
                {t("wl_upline_config_reward_rate")}: {formatAmount(row.rewards)}
              </div>
              <div className="overflow-x-auto">
                <table className="border-collapse text-[14px] w-full">
                  <thead>
                    <tr className="text-slate-500">
                      <th className="text-left pr-2 pb-1">
                        {t("wl_upline_config_tier")}
                      </th>
                      <th className="text-left px-2 pb-1">
                        {t("wl_upline_config_name")}
                      </th>
                      <th className="text-right px-2 pb-1">
                        {t("wl_tooltip_position_pct")}
                      </th>
                      <th className="text-right px-2 pb-1">
                        {t("wl_tooltip_fight_limit_config")}
                      </th>
                      <th className="text-right pl-2 pb-1">
                        {t("wl_tooltip_commission_pct")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tiers.map((key) => (
                      <tr key={key} className="border-t border-slate-100">
                        <td className="text-left pr-2 py-1 font-semibold text-slate-700">
                          {t(TIER_LABEL_KEY[key])}
                        </td>
                        <td className="text-left px-2 py-1 text-slate-600">
                          {row[key].username || "-"}
                        </td>
                        <td className="text-right px-2 py-1 text-sky-600">
                          {formatAmount(row[key].pct)}%
                        </td>
                        <td className="text-right px-2 py-1 text-amber-600">
                          {formatAmount(row[key].fightLimit)}
                        </td>
                        <td className="text-right pl-2 py-1 text-green-600">
                          {formatAmount(row[key].commissionPct)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          }
        >
          <AccountTreeIcon
            onClick={() => setOpen((prev) => !prev)}
            className="text-slate-400 shrink-0 cursor-pointer align-middle ml-1"
            sx={{ fontSize: 14 }}
          />
        </Tooltip>
      </span>
    </ClickAwayListener>
  );
};

const NumberWithTooltip = ({ content, className, children }) => {
  const [open, setOpen] = useState(false);
  const [expandedDetailIndex, setExpandedDetailIndex] = useState(null);
  if (!content) return <div className={className}>{children}</div>;
  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div>
        <Tooltip
          arrow
          placement="top"
          open={open}
          onClose={() => setOpen(false)}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#ffffff",
                color: "#1e293b",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                maxWidth: "none",
                width: "max-content",
              },
            },
            arrow: {
              sx: {
                color: "#ffffff",
                "&::before": {
                  border: "1px solid #e2e8f0",
                },
              },
            },
          }}
          title={
            <div className="text-[16px] leading-relaxed space-y-0.5">
              <div className="font-bold mb-1 text-slate-800 flex items-center gap-1 text-[18px]">
                <ViewListIcon
                  className="text-slate-500 shrink-0"
                  sx={{ fontSize: 16 }}
                />
                {content.title}
              </div>
              <hr />
              {content.lines.map((line, i) => (
                <TooltipLine
                  key={i}
                  line={line}
                  expanded={expandedDetailIndex === i}
                  onToggle={() =>
                    setExpandedDetailIndex((prev) => (prev === i ? null : i))
                  }
                />
              ))}
              <hr />
              {content.formula && (
                <div className="mt-1 italic text-slate-500 leading-snug whitespace-normal">
                  {content.formula}
                </div>
              )}
              <div
                className={`mt-1 font-bold ${content.resultColor || TOOLTIP_RESULT_COLOR}`}
              >
                = {content.result}
              </div>
            </div>
          }
        >
          <div
            onClick={() => setOpen((prev) => !prev)}
            className={`${className} cursor-pointer inline-flex items-center justify-end gap-1`}
          >
            <span>{children}</span>
            <InfoOutlinedIcon
              className="text-slate-400 shrink-0"
              sx={{ fontSize: 14 }}
            />
          </div>
        </Tooltip>
      </div>
    </ClickAwayListener>
  );
};

const GroupCells = ({ t, row, tierKey, currencyCode }) => {
  const group = row[tierKey];
  const isMember = tierKey === "member";
  const turnoverTooltip = group
    ? isMember
      ? buildMemberTurnoverTooltip(t, row)
      : buildTierTurnoverTooltip(t, row, tierKey)
    : null;
  const netAmtTooltip = group
    ? isMember
      ? buildMemberNetAmtTooltip(t, row)
      : buildTierNetAmtTooltip(t, row, tierKey)
    : null;
  const wlTooltip = group
    ? isMember
      ? buildMemberWlTooltip(t, row)
      : buildTierWlTooltip(t, row, tierKey)
    : null;

  return (
    <>
      <td className="p-2 text-right">
        <NumberWithTooltip
          content={turnoverTooltip}
          className="font-bold text-slate-700"
        >
          {formatAmount(group?.turnover)}
        </NumberWithTooltip>
        <NumberWithTooltip
          content={netAmtTooltip}
          className="font-bold text-slate-500"
        >
          {formatAmount(group?.netAmt)}
        </NumberWithTooltip>
      </td>
      <td className="p-2 text-right">
        <NumberWithTooltip content={wlTooltip} className={wlClass(group?.wl)}>
          {formatAmount(group?.wl)} {currencyCode}
        </NumberWithTooltip>
      </td>
    </>
  );
};

function WinloseDetail() {
  const { t } = useLanguage();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const username = searchParams.get("username");

  const [startdate, setStartdate] = useState(
    searchParams.get("startdate") || "",
  );
  const [enddate, setEnddate] = useState(searchParams.get("enddate") || "");
  const [number, setNumber] = useState(searchParams.get("number") || "");
  const [appliedStartdate, setAppliedStartdate] = useState(startdate);
  const [appliedEnddate, setAppliedEnddate] = useState(enddate);
  const [appliedNumber, setAppliedNumber] = useState(number);

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [currencyCode, setCurrencyCode] = useState("");

  // `username` is a member (opened from a member row) or an agent (opened
  // from a winlose tier row) — try the member profile first, then the agent's.
  useEffect(() => {
    if (!username) return;
    let cancelled = false;
    (async () => {
      const list = await ApiConfig.get("/getCurrencyList");
      if (cancelled || list.resCode !== 200) return;
      const codeById = Object.fromEntries(
        (list.data ?? []).map((c) => [c.id, c.code]),
      );
      const member = await ApiConfig.post("/profile", { user: username });
      let currencyId = member.resCode === 200 ? member.data?.currency_id : null;
      if (!currencyId) {
        const agent = await ApiConfig.get(
          `/agentprofile?user=${encodeURIComponent(username)}`,
        );
        currencyId = agent.resCode === 200 ? agent.data?.currency_id : null;
      }
      if (!cancelled) setCurrencyCode(codeById[currencyId] ?? "");
    })();
    return () => {
      cancelled = true;
    };
  }, [username]);

  // Totals across every row matching the current filters (not just the
  // visible page) — comes straight back on every /orderhistory response
  // (server-computed via SQL SUM, scoped by the same filters, ignoring
  // page/limit), so no separate full-result-set fetch is needed for it.
  const [tierTotals, setTierTotals] = useState(ZERO_TIER_TOTALS);

  useEffect(() => {
    setPage(1);
  }, [location.search]);

  const handleSearch = () => {
    setAppliedStartdate(startdate);
    setAppliedEnddate(enddate);
    setAppliedNumber(number);
    setPage(1);
  };

  // /orderhistory's `number` filter is optional — omitted entirely rather
  // than sent as the literal string "undefined" a plain URLSearchParams
  // object would produce for a missing value.
  const buildOrderHistoryParams = (pageNum, limitNum) => {
    const params = new URLSearchParams({
      user: username,
      startdate: appliedStartdate,
      enddate: appliedEnddate,
      page: pageNum,
      limit: limitNum,
    });
    if (appliedNumber) params.set("number", appliedNumber);
    return params;
  };

  // Exports every row matching the current filters (not just the visible
  // page) — fetched on demand only when this is clicked, since it's the
  // only feature on this page that needs every individual row rather than
  // just the totals.
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = buildOrderHistoryParams(1, 100000);
      const result = await ApiConfig.get(`/orderhistory?${params.toString()}`);
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          autoClose: 2000,
        });
        return;
      }
      const exportRows = buildRows(result.data);
      if (exportRows.length === 0) {
        toast.info(t("common_no_data"), { autoClose: 2000 });
        return;
      }
      const worksheet = buildExportWorksheet(t, exportRows, result.totals);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "WinLoseDetail");
      const datePart = [appliedStartdate, appliedEnddate]
        .filter(Boolean)
        .join("_to_");
      const filename = `winlose-detail-${username || "all"}${
        datePart ? `-${datePart}` : ""
      }.xlsx`;
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      toast.error(t("common_load_error"), { autoClose: 2000 });
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const params = buildOrderHistoryParams(page, limit);
    ApiConfig.get(`/orderhistory?${params.toString()}`).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          autoClose: 2000,
        });
        return;
      }
      setRows(buildRows(result.data));
      setTotal(result.total ?? 0);
      setTierTotals(result.totals || ZERO_TIER_TOTALS);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.search,
    appliedStartdate,
    appliedEnddate,
    appliedNumber,
    page,
    limit,
  ]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="shrink-0">
        <Navbar />
      </div>
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="shrink-0">
          <Header
            title={`${t("orderhistory_page_title")} - ${username ?? ""}`}
          />
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          <div className="p-2 bg-amber-50 border-[1px] border-slate-400 rounded-lg">
            <div className="p-2 flex items-center flex-wrap gap-2">
              <div className="p-2 border-[1px] border-slate-300 rounded-lg">
                <div className="flex flex-col mr-4">
                  <label className="font-bold mb-2">
                    {t("table_from_date")}
                  </label>
                  <div className="flex flex-row space-x-2">
                    <input
                      type="date"
                      className="p-1 border border-gray-300 rounded"
                      value={startdate}
                      onChange={(e) => setStartdate(e.target.value)}
                    />
                    <label className="font-bold mb-2">-</label>
                    <input
                      type="date"
                      className="p-1 border border-gray-300 rounded"
                      value={enddate}
                      onChange={(e) => setEnddate(e.target.value)}
                    />
                    <label className="font-bold mb-2">
                      {t("common_number_label")}
                    </label>
                    <input
                      type="text"
                      className="p-1 border border-gray-300 rounded"
                      value={number}
                      onChange={(e) => setNumber(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSearch();
                      }}
                      placeholder={t("orderhistory_search_number_placeholder")}
                    />
                    <button
                      onClick={handleSearch}
                      className="py-1 px-2 mt-2 bg-blue-500 text-white rounded"
                    >
                      {t("table_search_button")}
                    </button>
                    <button
                      onClick={handleExportExcel}
                      disabled={exporting}
                      className="flex items-center gap-1 py-1 px-2 mt-2 bg-green-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FileDownloadIcon sx={{ fontSize: 18 }} />
                      {exporting
                        ? t("common_exporting")
                        : t("common_export_excel")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="rounded overflow-hidden mt-1 overflow-x-auto">
            <table className="w-full text-[14px] border-collapse whitespace-nowrap">
              <thead>
                <tr className="bg-gray-100">
                  <th rowSpan={2} className="py-1 px-2 text-sm">
                    #
                  </th>
                  <th rowSpan={2} className="py-1 px-2 text-sm">
                    {t("lottobet_draw_date_label")}
                  </th>
                  <th rowSpan={2} className="py-1 px-2 text-sm">
                    {t("lottobet_market_label")}
                  </th>
                  <th rowSpan={2} className="py-1 px-2 text-sm">
                    {t("orderhistory_col_draw_id")}
                  </th>
                  <th rowSpan={2} className="py-1 px-2 text-sm">
                    {t("orderhistory_col_ticket_no")}
                  </th>
                  <th rowSpan={2} className="py-1 px-2 text-sm">
                    {t("orderhistory_col_transaction_time")}
                  </th>
                  <th rowSpan={2} className="py-1 px-2 text-sm">
                    {t("common_member")}
                  </th>
                  <th rowSpan={2} className="py-1 px-2 text-sm">
                    {t("lottobet_bet_type_label")}
                  </th>
                  <th rowSpan={2} className="py-1 px-2 text-sm">
                    {t("common_number_label")}
                  </th>
                  <th rowSpan={2} className="py-1 px-2 text-sm">
                    {t("waitresult_payout_rate_label")}
                  </th>
                  <th rowSpan={2} className="py-1 px-2 text-sm">
                    {t("winlose_col_bet_amount")}
                  </th>
                  <th
                    colSpan={2}
                    className="py-1 px-2 text-sm bg-teal-700 text-white"
                  >
                    {t("winlose_col_company")}
                  </th>
                  <th
                    colSpan={2}
                    className="py-1 px-2 text-sm bg-amber-700 text-white"
                  >
                    {t("winlose_col_senior")}
                  </th>
                  <th
                    colSpan={2}
                    className="py-1 px-2 text-sm bg-green-700 text-white"
                  >
                    {t("winlose_col_master")}
                  </th>
                  <th
                    colSpan={2}
                    className="py-1 px-2 text-sm bg-slate-800 text-white"
                  >
                    {t("winlose_col_agent")}
                  </th>
                  <th
                    colSpan={2}
                    className="py-1 px-2 text-sm bg-blue-800 text-white"
                  >
                    {t("common_member")}
                  </th>
                </tr>
                <tr className="bg-gray-100">
                  <th className="py-1 px-2 text-sm">
                    {t("orderhistory_col_turnover")} <br />
                    {t("orderhistory_col_net_amt")}
                  </th>
                  <th className="py-1 px-2 text-sm">
                    {t("winlose_col_win_loss")}
                  </th>
                  <th className="py-1 px-2 text-sm">
                    {t("orderhistory_col_turnover")} <br />
                    {t("orderhistory_col_net_amt")}
                  </th>
                  <th className="py-1 px-2 text-sm">
                    {t("winlose_col_win_loss")}
                  </th>
                  <th className="py-1 px-2 text-sm">
                    {t("orderhistory_col_turnover")} <br />
                    {t("orderhistory_col_net_amt")}
                  </th>
                  <th className="py-1 px-2 text-sm">
                    {t("winlose_col_win_loss")}
                  </th>
                  <th className="py-1 px-2 text-sm">
                    {t("orderhistory_col_turnover")} <br />{" "}
                    {t("orderhistory_col_net_amt")}
                  </th>
                  <th className="py-1 px-2 text-sm">
                    {t("winlose_col_win_loss")}
                  </th>
                  <th className="py-1 px-2 text-sm">
                    {t("orderhistory_col_turnover")}
                    <br /> {t("orderhistory_col_net_amt")}
                  </th>
                  <th className="py-1 px-2 text-sm">
                    {t("winlose_col_win_loss")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr
                    key={row.key}
                    className={`transition-colors ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-200"
                    } hover:bg-gray-300`}
                  >
                    <td className="p-2 text-center font-bold text-slate-700">
                      {index + 1}
                    </td>
                    <td className="p-2 text-center font-bold text-slate-500">
                      {row.drawDate
                        ? moment(row.drawDate).format("YYYY-MM-DD")
                        : ""}
                    </td>
                    <td className="p-2 text-center font-bold text-slate-500">
                      {lottoTypeKeyMap[row.market]
                        ? t(lottoTypeKeyMap[row.market])
                        : row.market}
                    </td>
                    <td className="p-2 text-center font-bold text-slate-500">
                      {row.drawId}
                    </td>
                    <td className="p-2 text-center font-bold text-slate-500">
                      {row.ticketNo}
                    </td>
                    <td className="p-2 text-center font-bold text-slate-500">
                      {row.transactionTime
                        ? moment(row.transactionTime).format(
                            "YYYY-MM-DD HH:mm:ss",
                          )
                        : ""}
                    </td>
                    <td className="p-2 text-center font-bold text-slate-700">
                      {row.player}
                      <MemberUplineConfigIcon t={t} row={row} />
                    </td>
                    <td className="p-2 text-center font-bold text-slate-500">
                      {betTypeKeyMap[row.game]
                        ? t(betTypeKeyMap[row.game])
                        : row.game}
                    </td>
                    <td className="p-2 text-center font-bold text-slate-700">
                      {formatNumberDisplay(t, row.number)}
                    </td>
                    <td className="p-2 text-center font-bold text-slate-500">
                      {formatAmount(row.rewards)} {currencyCode}
                    </td>
                    <td className="p-2 text-center font-bold text-slate-500">
                      {formatAmount(row.stake)} {currencyCode}
                    </td>
                    <GroupCells
                      t={t}
                      row={row}
                      tierKey="company"
                      currencyCode={currencyCode}
                    />
                    <GroupCells
                      t={t}
                      row={row}
                      tierKey="senior"
                      currencyCode={currencyCode}
                    />
                    <GroupCells
                      t={t}
                      row={row}
                      tierKey="master"
                      currencyCode={currencyCode}
                    />
                    <GroupCells
                      t={t}
                      row={row}
                      tierKey="agent"
                      currencyCode={currencyCode}
                    />
                    <GroupCells
                      t={t}
                      row={row}
                      tierKey="member"
                      currencyCode={currencyCode}
                    />
                  </tr>
                ))}
                <tr className="bg-slate-300 font-bold border-t-2 border-slate-500">
                  <td colSpan={11} className="p-2 text-right text-slate-800">
                    {t("table_total")}
                  </td>
                  {TIER_KEYS.map((key) => (
                    <React.Fragment key={key}>
                      <td className="p-2 text-right">
                        <div className="font-bold text-slate-700">
                          {formatAmount(tierTotals[key]?.turnover)}
                        </div>
                        <div className="font-bold text-slate-500">
                          {formatAmount(tierTotals[key]?.netAmt)}
                        </div>
                      </td>
                      <td
                        className={`p-2 text-right ${wlClass(tierTotals[key]?.wl)}`}
                      >
                        {formatAmount(tierTotals[key]?.wl)} {currencyCode}
                      </td>
                    </React.Fragment>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-2 px-1">
            <div className="flex items-center gap-2">
              <label className="font-bold text-sm">
                {t("common_per_page")}
              </label>
              <select
                className="p-1 border border-gray-300 rounded text-sm"
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="py-1 px-3 bg-blue-500 text-white rounded text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("common_page_previous")}
              </button>
              <span className="font-bold text-sm">
                {t("commission_page_label")} {page} / {totalPages} (
                {t("table_total")} {total} {t("common_items_unit")})
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="py-1 px-3 bg-blue-500 text-white rounded text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("common_next")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WinloseDetail;
